import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import type React from "react";
import type { Components } from "react-markdown";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import type { ReplacementRule } from "./utils";
import { dedentIndentedCodeBlocks, formatPageReferences, processCitationPlaceholders, processReplacements } from "./utils";
export type { ReplacementRule } from "./utils";

/**
 * Custom components for rendering markdown elements.
 * Provides consistent styling and special handling for page links.
 */
const createMarkdownComponents = (citationComponents?: Map<string, React.ReactElement>): Components => ({
  // Text elements - inject citations inline
  p: ({
    children
  }) => {
    const processedChildren = processCitationPlaceholders(children, citationComponents);
    return <p className="mb-2 last:mb-0">{processedChildren}</p>;
  },
  // Lists - with proper nesting indentation
  // Handle both nested lists (inside li) and sibling lists (after ol/ul)
  ul: ({
    children
  }) => <ul className="mb-2 ml-6 list-disc [&>li:last-child]:mb-4 [&_ul]:list-[circle] [&_ul_ul]:list-[square] [ol+&]:ml-12 [ul+&]:ml-12">
      {children}
    </ul>,
  ol: ({
    children,
    start
  }) => <ol className="mb-2 ml-6 list-decimal [&>li:last-child]:mb-4 [ol+&]:ml-12 [ul+&]:ml-12" start={start}>
      {children}
    </ol>,
  li: ({
    children
  }) => {
    const processedChildren = processCitationPlaceholders(children, citationComponents);
    return <li className="mb-1 pl-2">
        <div className="[&>ol:not(:first-child)]:ml-6 [&>ol]:mt-1 [&>ol]:ml-8 [&>p:not(:first-child)]:ml-6 [&>ul:not(:first-child)]:ml-6 [&>ul]:mt-1 [&>ul]:ml-8">
          {processedChildren}
        </div>
      </li>;
  },
  // Code blocks
  code: ({
    children,
    className,
    ...props
  }) => {
    const isCodeBlock = /language-(\\w+)/.test(className || "");
    if (isCodeBlock) {
      return <code className="block overflow-x-auto rounded bg-default-200 p-2 text-xs" {...props}>
          {children}
        </code>;
    }
    return <code className="rounded bg-default-200 px-1 py-0.5 text-xs" {...props}>
        {children}
      </code>;
  },
  pre: ({
    children
  }) => <pre className="mb-2 overflow-x-auto">{children}</pre>,
  // Quotes
  blockquote: ({
    children
  }) => <blockquote className="mb-2 border-default-300 border-l-4 pl-4 italic">{children}</blockquote>,
  // Headings
  h1: ({
    children
  }) => {
    const processedChildren = processCitationPlaceholders(children, citationComponents);
    return <h1 className="mb-2 font-bold text-xl">{processedChildren}</h1>;
  },
  h2: ({
    children
  }) => {
    const processedChildren = processCitationPlaceholders(children, citationComponents);
    return <h2 className="mb-2 font-bold text-lg">{processedChildren}</h2>;
  },
  h3: ({
    children
  }) => {
    const processedChildren = processCitationPlaceholders(children, citationComponents);
    return <h3 className="mb-2 font-bold text-base">{processedChildren}</h3>;
  },
  // Inline formatting - must process citations to handle cases like **text [[D:1|P:2]]**
  strong: ({
    children
  }) => {
    const processedChildren = processCitationPlaceholders(children, citationComponents);
    return <strong>{processedChildren}</strong>;
  },
  em: ({
    children
  }) => {
    const processedChildren = processCitationPlaceholders(children, citationComponents);
    return <em>{processedChildren}</em>;
  },
  // Links - use Next.js Link for internal page navigation
  a: ({
    children,
    href
  }) => {
    // Check if this is an internal page link (has page query param)
    const isPageLink = href?.includes("page=");
    if (isPageLink) {
      return <Link href={href || "#"} className="text-primary hover:underline" prefetch={false}>
          {children}
        </Link>;
    }

    // External or other links
    return <a href={href} className="text-primary hover:underline" target={href?.startsWith("http") ? "_blank" : undefined} rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}>
        {children}
      </a>;
  },
  // Tables - inject citations into table cells
  table: ({
    children
  }) => <table className="mb-2 min-w-full border-collapse border border-default-200">{children}</table>,
  thead: ({
    children
  }) => <thead className="bg-default-100">{children}</thead>,
  tbody: ({
    children
  }) => <tbody>{children}</tbody>,
  tr: ({
    children
  }) => <tr className="border-default-200 border-b">{children}</tr>,
  th: ({
    children
  }) => {
    const processedChildren = processCitationPlaceholders(children, citationComponents);
    return <th className="border border-default-200 px-4 py-2 text-left font-semibold">{processedChildren}</th>;
  },
  td: ({
    children
  }) => {
    const processedChildren = processCitationPlaceholders(children, citationComponents);
    return <td className="border border-default-200 px-4 py-2">{processedChildren}</td>;
  }
});
interface MarkdownRendererServerProps {
  content: string;
  /**
   * The current pathname for generating page links.
   */
  pathname: string;
  /**
   * The current search params for generating page links.
   */
  searchParams: URLSearchParams;
  /**
   * Generic replacement rules for matching patterns and rendering custom components.
   * Each rule specifies a regex pattern and a render function.
   */
  replacements?: ReplacementRule[];
}

/**
 * Server component version of MarkdownRenderer.
 * Renders markdown content with special handling for page references and custom replacements.
 * Automatically converts page references like "page 1" into clickable links.
 * Use the `replacements` prop to define custom pattern matching and component rendering.
 */
export const MarkdownRendererServer = t0 => {
  const $ = _c(11);
  const {
    content,
    pathname,
    searchParams,
    replacements
  } = t0;
  let t1;
  let textWithPlaceholders;
  if ($[0] !== content || $[1] !== pathname || $[2] !== replacements || $[3] !== searchParams) {
    const processedContent = formatPageReferences(dedentIndentedCodeBlocks(content), {
      pathname,
      searchParams
    });
    const componentMap = new Map();
    let placeholderCounter = 0;
    textWithPlaceholders = processedContent;
    if (replacements?.length) {
      const parts = processReplacements(processedContent, replacements);
      textWithPlaceholders = "";
      for (const part of parts) {
        if (typeof part === "string") {
          textWithPlaceholders = textWithPlaceholders + part;
        } else {
          const placeholder = `§COMPONENT_INDEX_${placeholderCounter}§`;
          componentMap.set(placeholder, part);
          textWithPlaceholders = textWithPlaceholders + placeholder;
          placeholderCounter++;
        }
      }
    }
    t1 = createMarkdownComponents(componentMap);
    $[0] = content;
    $[1] = pathname;
    $[2] = replacements;
    $[3] = searchParams;
    $[4] = t1;
    $[5] = textWithPlaceholders;
  } else {
    t1 = $[4];
    textWithPlaceholders = $[5];
  }
  const components = t1;
  let t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [remarkGfm];
    t3 = [rehypeRaw];
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  let t4;
  if ($[8] !== components || $[9] !== textWithPlaceholders) {
    t4 = <ReactMarkdown remarkPlugins={t2} rehypePlugins={t3} components={components}>{textWithPlaceholders}</ReactMarkdown>;
    $[8] = components;
    $[9] = textWithPlaceholders;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};